import {
	AlertBar,
	Checkbox,
	FormItem,
	InputWithMoreInfo,
	RadioButtonGroup,
	RadioButtonGroupItem,
	SecretValueInput,
	Select,
	SelectItem,
	TextInput,
	Toolbar,
	ValidationError,
	useAlertDialog
} from "client/Components";
import { NoUniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";
import { IFormHandler } from "client/Forms";
import { IInputAttachable, IValueInputAttachable } from "client/Forms/attachable";
import {
	CreateOAuth2ApplicationInAzureModel,
	OAuth2ConfigurationSaveModel,
	WsFederationConfigurationSaveModel,
	SignOutRedirectUrlConfiguration,
	SingleSignOnType,
	CustomSignOutRedirectUrlActionType,
	ServiceHostingLocation
} from "client/Api/SingleSignOnSettings/singleSignOnSettings";
import { SecretValue } from "client/Api/Apps/common";
import { localize } from "client_legacy/ndaw/culture";
import { navigateTo } from "client_legacy/ndaw/navigator";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";

export type SingleSignOnSettingsControlProps = {
	formHandler: IFormHandler<SingleSignOnFormData>;
	availableSingleSignOnTypes: readonly SingleSignOnType[];
	configurationHelpLink: string;
	signoutUrlHelpLink: string;
	activeDirectoryHostingLocation: ServiceHostingLocation;
	hasCredentialsForCurrentUser: boolean;
	isAutomaticSetupAvailable: boolean;
	productName: string;
	onVerifyOAuth2: () => void;
	onEnableMicrosoftAuthenticatorMobileSignIn: () => void;
	hasUnsavedOAuth2CredentialsChanges: boolean;
};

export const SingleSignOnSettingsControl = (props: SingleSignOnSettingsControlProps): ReactElement => {
	const {
		formHandler,
		availableSingleSignOnTypes,
		configurationHelpLink,
		signoutUrlHelpLink,
		activeDirectoryHostingLocation,
		hasCredentialsForCurrentUser,
		isAutomaticSetupAvailable,
		productName,
		onVerifyOAuth2,
		onEnableMicrosoftAuthenticatorMobileSignIn,
		hasUnsavedOAuth2CredentialsChanges
	} = props;

	const kindOptions = useMemo(
		(): readonly SelectItem<SingleSignOnType>[] => {
			return availableSingleSignOnTypes.map(
				(x): SelectItem<SingleSignOnType> => ({
					value: x,
					text: singleSignOnTypeToNameMap[x]
				})
			);
		},
		[availableSingleSignOnTypes]
	);

	const moreInfoHandler = useCallback(
		(): void => {
			navigateTo(configurationHelpLink, true);
		},
		[configurationHelpLink]
	);

	const kindAttachable: IInputAttachable<SingleSignOnType> = formHandler.for("singleSignOnType");

	return (
		<>
			<FormItem label={localize("_SingleSignOnType")}>
				<InputWithMoreInfo onMoreInfo={moreInfoHandler}>
					<Select
						{...kindAttachable.attachToSelect()}
						options={kindOptions}
						menuTitle={localize("_SingleSignOnType")}
					/>
				</InputWithMoreInfo>

				<ValidationError {...kindAttachable.attachToValidationError()} />
			</FormItem>

			{kindAttachable.value === SingleSignOnType.OAuth2 && (
				<OAuth2Configuration
					attachable={formHandler.for("oauth2")}
					activeDirectoryHostingLocation={activeDirectoryHostingLocation}
					hasCredentialsForCurrentUser={hasCredentialsForCurrentUser}
					signoutUrlHelpLink={signoutUrlHelpLink}
					isAutomaticSetupAvailable={isAutomaticSetupAvailable}
					productName={productName}
					onVerify={onVerifyOAuth2}
					onEnableMicrosoftAuthenticatorMobileSignIn={onEnableMicrosoftAuthenticatorMobileSignIn}
					hasUnsavedOAuth2CredentialsChanges={hasUnsavedOAuth2CredentialsChanges}
				/>
			)}

			{kindAttachable.value === SingleSignOnType.WsFederation && (
				<WsFedConfiguration
					attachable={formHandler.for("wsFed")}
					signoutUrlHelpLink={signoutUrlHelpLink}
				/>
			)}
		</>
	);
};
SingleSignOnSettingsControl.displayName = "SingleSignOnSettingsControl";

const singleSignOnTypeToNameMap: { [key in SingleSignOnType]: string } = {
	[SingleSignOnType.WsFederation]: localize("_WebServicesFederation") + " (" + localize("_OnPremisesOnly") + ")",
	[SingleSignOnType.OAuth2]: localize("_OAuth2") + " (" + localize("_Recommended") + ")"
};

export type SingleSignOnFormData = {
	singleSignOnType: SingleSignOnType;
	oauth2: OAuth2FormData;
	wsFed: WsFedFormData;
};

export type OAuth2ConfigurationProps = {
	attachable: IValueInputAttachable<OAuth2FormData>;
	activeDirectoryHostingLocation: ServiceHostingLocation;
	hasCredentialsForCurrentUser: boolean;
	isAutomaticSetupAvailable: boolean;
	signoutUrlHelpLink: string;
	productName: string;
	onVerify: () => void;
	onEnableMicrosoftAuthenticatorMobileSignIn: () => void;
	hasUnsavedOAuth2CredentialsChanges: boolean;
};

export const OAuth2Configuration = (props: OAuth2ConfigurationProps): ReactElement => {
	const {
		attachable,
		activeDirectoryHostingLocation,
		hasCredentialsForCurrentUser,
		isAutomaticSetupAvailable,
		signoutUrlHelpLink,
		productName,
		onVerify,
		onEnableMicrosoftAuthenticatorMobileSignIn,
		hasUnsavedOAuth2CredentialsChanges
	} = props;

	const [microsoftAuthenticatorInfoDialog, openMicrosoftAuthenticatorInfoDialog] = useAlertDialog();

	const openMicrosoftAuthenticatorInfoDialogHandler = useCallback(
		async (): Promise<void> => {
			await openMicrosoftAuthenticatorInfoDialog({
				title: localize("_MobileAppSignInWithMicrosoftAuthenticator"),
				children: localize("_MobileAppSignInWithMicrosoftAuthenticatorInfo"),
				showCloseButton: true,
				trackingId: "mobile-app-microsoft-authenticator-sign-in-info-dialog"
			});
		},
		[openMicrosoftAuthenticatorInfoDialog]
	);

	return (
		<>
			{isAutomaticSetupAvailable && (
				<FormItem isForGroup>
					<RadioButtonGroup
						{...attachable.for("mode").attachToRadioButtonGroup()}
						options={oauth2ModeOptions}
					/>

					<ValidationError {...attachable.for("mode").attachToValidationError()} />
				</FormItem>
			)}

			{attachable.for("mode").value === SingleSignOnConfigurationMode.Automatic && (
				<OAuth2AutomaticConfiguration
					attachable={attachable.for("automatic")}
					activeDirectoryHostingLocation={activeDirectoryHostingLocation}
					hasCredentialsForCurrentUser={hasCredentialsForCurrentUser}
					productName={productName}
					onOpenMicrosoftAuthenticatorInfoDialog={openMicrosoftAuthenticatorInfoDialogHandler}
				/>
			)}

			{attachable.for("mode").value === SingleSignOnConfigurationMode.Manual && (
				<OAuth2ManualConfiguration
					attachable={attachable.for("manual")}
					signoutUrlHelpLink={signoutUrlHelpLink}
					onVerify={onVerify}
					onEnableMicrosoftAuthenticatorMobileSignIn={onEnableMicrosoftAuthenticatorMobileSignIn}
					onOpenMicrosoftAuthenticatorInfoDialog={openMicrosoftAuthenticatorInfoDialogHandler}
					hasUnsavedOAuth2CredentialsChanges={hasUnsavedOAuth2CredentialsChanges}
				/>
			)}

			{microsoftAuthenticatorInfoDialog}
		</>
	);
};
OAuth2Configuration.displayName = "OAuth2Configuration";

export enum SingleSignOnConfigurationMode {
	Manual = 0,
	Automatic = 1
}

export type OAuth2FormData = {
	mode: SingleSignOnConfigurationMode;
	manual: OAuth2ManualFormData;
	automatic: CreateOAuth2ApplicationInAzureModel;
};

const oauth2ModeOptions: readonly RadioButtonGroupItem<SingleSignOnConfigurationMode>[] = [
	{
		text: localize("_AutomaticSetup"),
		value: SingleSignOnConfigurationMode.Automatic
	},
	{
		text: localize("_ManualSetup"),
		value: SingleSignOnConfigurationMode.Manual
	}
];

export type OAuth2AutomaticConfigurationProps = {
	attachable: IValueInputAttachable<CreateOAuth2ApplicationInAzureModel>;
	hasCredentialsForCurrentUser: boolean;
	activeDirectoryHostingLocation: ServiceHostingLocation;
	productName: string;
	onOpenMicrosoftAuthenticatorInfoDialog: () => Promise<void>;
};

export const OAuth2AutomaticConfiguration = (props: OAuth2AutomaticConfigurationProps): ReactElement => {
	const {
		attachable,
		hasCredentialsForCurrentUser,
		activeDirectoryHostingLocation,
		productName,
		onOpenMicrosoftAuthenticatorInfoDialog
	} = props;

	return (
		<>
			{!hasCredentialsForCurrentUser && (
				<SsoPassword
					attachable={attachable.for("password")}
					activeDirectoryHostingLocation={activeDirectoryHostingLocation}
				/>
			)}

			<FormItem>
				<Checkbox
					{...attachable.for("permissionToCreateAzureApplication").attachToCheckbox()}
					ellipseText={false}
					text={{
						template: localize("_IGiveProductNamePermissionsToCreateAnAzureApplication"),
						replacements: [
							{ what: "{ProductName}", filler: productName }
						]
					}}
				/>

				<ValidationError {...attachable.for("permissionToCreateAzureApplication").attachToValidationError()} />
			</FormItem>

			<FormItem label={localize("_GrantPermissionTo")}>
				<NoUniqueInputIdContext>
					<Checkbox
						{...attachable.for("userProfilePermissions").attachToCheckbox()}
						text={localize("_UserProfile")}
					/>

					<Checkbox
						{...attachable.for("sharePointPermissions").attachToCheckbox()}
						text={localize("_SharePoint")}
					/>

					<Checkbox
						{...attachable.for("exchangePermissions").attachToCheckbox()}
						text={localize("_Exchange")}
					/>

					<Checkbox
						{...attachable.for("powerBiPermissions").attachToCheckbox()}
						text={localize("_PowerBI")}
					/>

					<Checkbox
						{...attachable.for("windowsVirtualDesktopPermissions").attachToCheckbox()}
						text={localize("_AzureVirtualDesktop")}
					/>

					<Checkbox
						{...attachable.for("windowsVirtualDesktopArmPermissions").attachToCheckbox()}
						text={localize("_AzureVirtualDesktopArm")}
					/>
				</NoUniqueInputIdContext>
			</FormItem>

			<FormItem label={localize("_MobileAppSignInMethod")}>
				<InputWithMoreInfo
					onMoreInfo={onOpenMicrosoftAuthenticatorInfoDialog}
					trackingId="mobile-app-microsoft-authenticator-sign-in-info"
				>
					<Checkbox
						{...attachable.for("shouldEnableMicrosoftAuthenticatorMobileSignIn").attachToCheckbox()}
						text={localize("_EnableMicrosoftAuthenticatorSignIn")}
					/>
				</InputWithMoreInfo>
				<ValidationError {...attachable.for("shouldEnableMicrosoftAuthenticatorMobileSignIn").attachToValidationError()} />
			</FormItem>
		</>
	);
};
OAuth2AutomaticConfiguration.displayName = "OAuth2AutomaticConfiguration";

export type OAuth2ManualConfigurationProps = {
	attachable: IValueInputAttachable<OAuth2ManualFormData>;
	signoutUrlHelpLink: string;
	onVerify: () => void;
	onEnableMicrosoftAuthenticatorMobileSignIn: () => void;
	onOpenMicrosoftAuthenticatorInfoDialog: () => Promise<void>;
	hasUnsavedOAuth2CredentialsChanges: boolean;
};

export const OAuth2ManualConfiguration = (props: OAuth2ManualConfigurationProps): ReactElement => {
	const {
		attachable,
		signoutUrlHelpLink,
		onVerify,
		onEnableMicrosoftAuthenticatorMobileSignIn,
		onOpenMicrosoftAuthenticatorInfoDialog,
		hasUnsavedOAuth2CredentialsChanges
	} = props;

	const [isClientSecretAlertVisible, setIsClientSecretAlertVisible] = useState<boolean>(false);

	const enforceToClientSecret = (): void => {
		const keyAttachable: IValueInputAttachable<SecretValue> = attachable.for("key");

		const { containsNewValue } = keyAttachable.value;
		if (containsNewValue) {
			return;
		}

		keyAttachable.reducer
			.setValue({
				containsNewValue: true,
				value: ""
			});

		keyAttachable.validation
			.triggerInputFinished();

		setIsClientSecretAlertVisible(true);
	};

	return (
		<>
			<FormItem
				label={localize("_Authority")}
				isRequired
			>
				<TextInput
					{...attachable.for("authority")
						.attachToTextInput()
						.withOnChange(enforceToClientSecret)
					}
					placeholder="https://login.windows.net/domain.com/"
				/>

				<ValidationError {...attachable.for("authority").attachToValidationError()} />
			</FormItem>

			<FormItem
				label={localize("_ClientId")}
				isRequired
			>
				<TextInput
					{...attachable.for("clientId").attachToTextInput()}
				/>

				<ValidationError {...attachable.for("clientId").attachToValidationError()} />
			</FormItem>

			<FormItem
				label={localize("_Key")}
				isRequired
			>
				<SecretValueInput
					{...attachable.for("key").attachToSecretValueInput()}
					containsInitialValue={attachable.for("doesKeyHaveValueOnServer").value}
				/>

				<ValidationError
					{...attachable.for("key")
						.for("value")
						.attachToValidationError()
					}
				/>
			</FormItem>

			{isClientSecretAlertVisible && (
				<FormItem>
					<AlertBar
						isInline
						kind="info"
						onDismiss={() => setIsClientSecretAlertVisible(true)}
					>
						{localize("_SinceTheAuthorityHasBeenChangedYouNneedToFillInTheKeyWhichBelongsToTheNewAuthority")}
					</AlertBar>
				</FormItem>
			)}

			<FormItem
				label={localize("_DomainHint")}
			>
				<TextInput
					{...attachable.for("domainHint").attachToTextInput()}
					placeholder="domain.com"
				/>

				<ValidationError {...attachable.for("domainHint").attachToValidationError()} />
			</FormItem>

			<FormItem>
				<Checkbox
					{...attachable.for("shouldSignOutWhenRefreshTokenExpires").attachToCheckbox()}
					text={localize("_AutomaticallySignOutAUserWhenTheRefreshTokenIsExpired")}
				/>

				<ValidationError {...attachable.for("shouldSignOutWhenRefreshTokenExpires").attachToValidationError()} />
			</FormItem>

			<FormItem>
				<Toolbar horizontalAlignment="left">
					<Toolbar.Button
						kind="primary"
						text={localize("_Verify")}
						trackingId="verify"
						onClick={onVerify}
					/>
				</Toolbar>

				<ValidationError {...attachable.for("wasVerifyClickedAtleastOnce").attachToValidationError()} />
			</FormItem>

			<FormItem>
				<Checkbox
					{...attachable.for("isVerified").attachToCheckbox()}
					text={localize("_IHaveSeenTheGreenScreenTellingTheVerificationWasSuccessful")}
				/>

				<ValidationError {...attachable.for("isVerified").attachToValidationError()} />
			</FormItem>

			<AdditionalSettings
				attachable={attachable.for("signOutRedirectUrlConfiguration")}
				signoutUrlHelpLink={signoutUrlHelpLink}
			/>

			<FormItem label={localize("_MobileAppSignInMethod")}>
				<InputWithMoreInfo
					onMoreInfo={onOpenMicrosoftAuthenticatorInfoDialog}
					trackingId="mobile-app-microsoft-authenticator-sign-in-info"
				>
					{attachable.for("isMicrosoftAuthenticatorMobileSignInEnabled").value
						? localize("_SignInWithMicrosoftAuthenticatorIsEnabled")
						: (
							<Toolbar horizontalAlignment="left">
								<Toolbar.Button
									kind="primary"
									text={localize("_EnableMicrosoftAuthenticatorSignIn")}
									trackingId="enable-microsoft-authenticator-mobile-sign-in"
									onClick={onEnableMicrosoftAuthenticatorMobileSignIn}
									isDisabled={hasUnsavedOAuth2CredentialsChanges}
								/>
							</Toolbar>
						)}
				</InputWithMoreInfo>
			</FormItem>
		</>
	);
};
OAuth2ManualConfiguration.displayName = "OAuth2ManualConfiguration";

export type OAuth2ManualFormData = OAuth2ConfigurationSaveModel & {
	wasVerifyClickedAtleastOnce: boolean;
	isVerified: boolean;
	doesKeyHaveValueOnServer: boolean;
	isMicrosoftAuthenticatorMobileSignInEnabled: boolean;
};

export type WsFedConfigurationProps = {
	attachable: IValueInputAttachable<WsFederationConfigurationSaveModel>;
	signoutUrlHelpLink: string;
};

export const WsFedConfiguration = (props: WsFedConfigurationProps): ReactElement => {
	const {
		attachable,
		signoutUrlHelpLink
	} = props;

	return (
		<>
			<FormItem
				label={localize("_ActiveDirectoryFederationMetadataDocumentUrl")}
				isRequired
			>
				<TextInput
					{...attachable.for("metadataDocumentUrl").attachToTextInput()}
				/>

				<ValidationError {...attachable.for("metadataDocumentUrl").attachToValidationError()} />
			</FormItem>

			<AdditionalSettings
				attachable={attachable.for("signOutRedirectUrlConfiguration")}
				signoutUrlHelpLink={signoutUrlHelpLink}
			/>
		</>
	);
};
WsFedConfiguration.displayName = "WsFedConfiguration";

export type WsFedFormData = WsFederationConfigurationSaveModel;

export type AdditionalSettingsProps = {
	attachable: IValueInputAttachable<SignOutRedirectUrlConfiguration>;
	signoutUrlHelpLink: string;
};

export const AdditionalSettings = (props: AdditionalSettingsProps): ReactElement | null => {
	const {
		attachable,
		signoutUrlHelpLink
	} = props;

	const moreInfoHandler = useCallback(
		(): void => {
			navigateTo(signoutUrlHelpLink, true);
		},
		[signoutUrlHelpLink]
	);

	const actionAttachable: IValueInputAttachable<CustomSignOutRedirectUrlActionType> = attachable.for("action");

	const urlChangeHandler = useCallback(
		(value: string): void => {
			const action: CustomSignOutRedirectUrlActionType = value
				? CustomSignOutRedirectUrlActionType.Enabled
				: CustomSignOutRedirectUrlActionType.Disabled;

			actionAttachable
				.reducer
				.setValue(action);

			actionAttachable.validation
				.triggerInputFinished();
		},
		[actionAttachable]
	);

	if (actionAttachable.value === CustomSignOutRedirectUrlActionType.None) {
		return null;
	}

	return (
		<>
			<h2 className="title">
				{localize("_AdditionalSettings")}
			</h2>

			<FormItem
				label={localize("_SignOutRedirectUrl")}
			>
				<InputWithMoreInfo onMoreInfo={moreInfoHandler}>
					<TextInput
						{...attachable.for("url")
							.attachToTextInput()
							.withOnChange(urlChangeHandler)
						}
					/>
				</InputWithMoreInfo>

				<ValidationError {...attachable.for("url").attachToValidationError()} />
			</FormItem>
		</>
	);
};
AdditionalSettings.displayName = "AdditionalSettings";

export type SsoPasswordProps = {
	attachable: IValueInputAttachable<string | null>;
	activeDirectoryHostingLocation: ServiceHostingLocation;
};

export const SsoPassword = (props: SsoPasswordProps): ReactElement => {
	const {
		attachable,
		activeDirectoryHostingLocation
	} = props;

	const [alertDialog, openAlertDialog] = useAlertDialog();

	const passwordInfoHandler = useCallback(
		async (): Promise<void> => {
			let message: string;
			if (activeDirectoryHostingLocation === ServiceHostingLocation.Office365) {
				message = localize("_Office365PasswordDialogExplanation");
			} else {
				message = localize("_ActiveDirectoryPasswordDialogExplanation");
			};

			await openAlertDialog({
				title: localize("_YourPasswordIsSafeWithUs"),
				children: message,
				showCloseButton: true,
				trackingId: "sso-password-info-dialog"
			});
		},
		[activeDirectoryHostingLocation, openAlertDialog]
	);

	return (
		<>
			<FormItem
				label={localize("_Password")}
				isRequired
			>
				<InputWithMoreInfo onMoreInfo={passwordInfoHandler}>
					<TextInput
						{...attachable.attachToTextInput()}
						type="password"
					/>
				</InputWithMoreInfo>

				<ValidationError {...attachable.attachToValidationError()} />
			</FormItem>

			{alertDialog}
		</>
	);
};
SsoPassword.displayName = "SsoPassword";
